import axios from "axios";

const API_URL = process.env.REACT_APP_HOST_API;

// In-flight request coalescing (prevents duplicate calls on mount / StrictMode)
// We only coalesce known idempotent endpoints to avoid masking side effects.
const inFlight = new Map();
const shouldCoalesce = (method, url) => {
  const u = String(url || "");
  if (!u) return false;

  // Expensive search endpoints (POST but idempotent)
  if (method === "post" && (u.includes("AdvancedSearch/SearchDocuments") || u.includes("AdvancedSearch/SearchArchives"))) {
    return true;
  }

  // Explorer initial load endpoints (GET and idempotent)
  if (method === "get" && (u.includes("Archives/TreeResource") || u.includes("Documents/ByDetailFolderResource"))) {
    return true;
  }

  return false;
};

const makeKey = (method, url, data, config) => {
  // Keep it stable, but avoid huge keys.
  const u = String(url || "");
  let body = "";
  try {
    if (data !== undefined) body = JSON.stringify(data);
  } catch {
    body = "[unstringifiable]";
  }
  // Include relevant query/config fields if present.
  const params = config?.params ? JSON.stringify(config.params) : "";
  return `${method}:${u}:${params}:${body}`;
};

const setToken = () => {
  if (localStorage.getItem("docplace")) {
    return "Bearer " + JSON.parse(localStorage.getItem("docplace")).token;
  }
};

const setUserId = () => {
  if (localStorage.getItem("docplace")) {
    return JSON.parse(localStorage.getItem("docplace")).userId;
  }
};

const setCustomerId = () => {
  if (localStorage.getItem("docplace")) {
    return JSON.parse(localStorage.getItem("docplace")).customerId;
  }
};

const setDoctypeId = () => {
  if (localStorage.getItem("docplace")) {
    return JSON.parse(localStorage.getItem("docplace")).doctypeId;
  }
};

const axiosApi = axios.create({
  baseURL: API_URL,
});

// Request interceptor to handle endpoint-specific header requirements
axiosApi.interceptors.request.use(
  async (config) => {
    // AdvancedSearch endpoints don't accept doctypeId or name headers
    // Remove them for these endpoints to prevent 400 errors
    const advancedSearchEndpoints = [
      'AdvancedSearch/Search',
      'AdvancedSearch/SearchArchives',
      'AdvancedSearch/SearchDocuments'
    ];

    const isAdvancedSearchEndpoint = advancedSearchEndpoints.some(endpoint =>
      config.url && config.url.includes(endpoint)
    );

    if (isAdvancedSearchEndpoint && config.headers) {
      // Remove invalid headers - handle both camelCase and lowercase
      delete config.headers['doctypeId'];
      delete config.headers['doctypeid'];
      delete config.headers['name'];
      delete config.headers['Name'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosApi.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

const setHeaders = () => {
  axiosApi.defaults.headers.common["Authorization"] = setToken();
  axiosApi.defaults.headers.common["customerId"] = setCustomerId();
  axiosApi.defaults.headers.common["doctypeId"] = setDoctypeId();
  axiosApi.defaults.headers.common["userId"] = setUserId();
  axiosApi.defaults.headers.common["name"] = "doc";
  // Clear documentId header to prevent stale values from persisting across requests
  delete axiosApi.defaults.headers.common["documentId"];
};

export async function get(url, config = {}) {
  setHeaders();
  if (!shouldCoalesce("get", url)) {
    return await axiosApi.get(url, { ...config }).then((response) => response.data);
  }

  const key = makeKey("get", url, undefined, config);
  const existing = inFlight.get(key);
  if (existing) return await existing;

  const p = axiosApi
    .get(url, { ...config })
    .then((response) => response.data)
    .finally(() => {
      inFlight.delete(key);
    });
  inFlight.set(key, p);
  return await p;
}

export async function getDoc(url, config = {}) {
  setHeaders();
  return await axiosApi
    .get(url, { responseType: "blob", ...config })
    .then((response) => response.data);
}

export async function post(url, data, config = {}) {
  setHeaders();
  // Fix: Extract documentId string from config object - ensure it's a valid string
  if (config && config.documentId && typeof config.documentId === 'string') {
    axiosApi.defaults.headers.common["documentId"] = config.documentId;
  }
  if (!shouldCoalesce("post", url)) {
    return axiosApi.post(url, { ...data }, { ...config }).then((response) => response.data);
  }

  const key = makeKey("post", url, data, config);
  const existing = inFlight.get(key);
  if (existing) return await existing;

  const p = axiosApi
    .post(url, { ...data }, { ...config })
    .then((response) => response.data)
    .finally(() => {
      inFlight.delete(key);
    });
  inFlight.set(key, p);
  return await p;
}

export async function post_(url, data, config = {}) {
  setHeaders();
  // Fix: Extract documentId string from config object - ensure it's a valid string
  if (config && config.documentId && typeof config.documentId === 'string') {
    axiosApi.defaults.headers.common["documentId"] = config.documentId;
  }
  return axiosApi
    .post(url, [...data], { ...config })
    .then((response) => response.data);
}

export async function postDownload(url, data, config = {}) {
  setHeaders();
  // Fix: Extract documentId string from config object - ensure it's a valid string
  if (config && config.documentId && typeof config.documentId === 'string') {
    axiosApi.defaults.headers.common["documentId"] = config.documentId;
  }
  return axiosApi
    .post(url, [...data], { ...config, responseType: "blob" })
    .then((response) => response.data);
}

export async function dpost(url, data, config = {}) {
  setHeaders();
  return axiosApi
    .post(url, data, { ...config })
    .then((response) => response.data);
}
export async function put(url, data, config = {}) {
  setHeaders();
  return axiosApi
    .put(url, { ...data }, { ...config })
    .then((response) => response.data);
}

export async function patch(url, data, config = {}) {
  setHeaders();
  return axiosApi
    .patch(url, { ...data }, { ...config })
    .then((response) => response.data);
}

export async function del(url, data, config = {}) {
  setHeaders();
  return axiosApi
    .delete(url, { ...config, data })
    .then((response) => response.data);
}

export async function del_(url) {
  setHeaders();
  return axiosApi.delete(url).then((response) => response.data);
}
